import { useState } from "react";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { EntityPicker, type PickedEntity } from "@/components/ui/EntityPicker";
import { Icon } from "@/components/ui/Icon";
import { request } from "@/lib/api/client";
import { useList } from "@/lib/api/queries";
import { useQueryClient } from "@tanstack/react-query";

/**
 * Qo'lda prixod yoki zakas yaratish.
 *
 * Odatda hujjat 1C dan keladi. Lekin WMS 1C ga BOG'LIQ bo'lib
 * qolmasligi kerak: 1C tomoni ulanmagan, to'xtagan yoki hujjat
 * umuman 1C da yo'q bo'lgan holatlarda ham ombor ishlashi shart.
 * Server buni ilgaridan qo'llab-quvvatlaydi — qo'lda kiritish 1C
 * bilan BIR XIL endpointga boradi (P01-001: qoldiq bu yerda
 * oshmaydi), bu oyna faqat shu yo'lni panelga chiqaradi.
 *
 * Yaratilgan hujjat o'zidan ishga aylanmaydi: kirim —
 * «Mashina keldi», chiqim — «Terishga chiqarish» bosilganda
 * TSD ga topshiriq tushadi. Shuning uchun oynaning pastida
 * keyingi qadam yozib qo'yilgan.
 */

interface Sku {
  id: string;
  article: string;
  name: string;
  base_uom?: string;
}

interface Counterparty {
  id: string;
  name: string;
}

interface Warehouse {
  id: string;
  code: string;
  name?: string;
}

interface Line {
  /** Formadagi qator kaliti — SKU almashsa ham o'zgarmaydi. */
  key: string;
  sku: PickedEntity | null;
  qty: string;
}

const BLANK = (): Line => ({ key: crypto.randomUUID(), sku: null, qty: "" });

export function NewOrder({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const [number, setNumber] = useState("");
  const [party, setParty] = useState<PickedEntity | null>(null);
  const [date, setDate] = useState("");
  const [warehouse, setWarehouse] = useState("");
  const [note, setNote] = useState("");
  const [lines, setLines] = useState<Line[]>([BLANK()]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const warehouses = useList<Warehouse>("topology", "/api/v1/warehouses", { limit: 50 }, { enabled: open });
  const codes = (warehouses.data?.items ?? []).map((row) => row.code);
  const code = warehouse || codes[0] || "A";

  const filled = lines.filter((line) => line.sku !== null && line.qty.trim() !== "");
  const ready = number.trim() !== "" && filled.length > 0;

  const reset = () => {
    setNumber("");
    setParty(null);
    setDate("");
    setWarehouse("");
    setNote("");
    setLines([BLANK()]);
    setError(null);
  };

  const close = () => {
    reset();
    onClose();
  };

  const submit = () => {
    setBusy(true);
    setError(null);

    // 1C GUIDi yo'q, shuning uchun tashqi kalit RAQAMDAN yasaladi:
    // u takrorlanmas va bir xil raqamni ikki marta yuborish ikkinchi
    // hujjat yaratmaydi (server `external_id` bo'yicha upsert qiladi).
    const externalID = `MANUAL-${number.trim()}`;
    const body = {
      operation_id: crypto.randomUUID(),
      external_id: externalID,
      order_number: number.trim(),
      customer_id: party?.id || undefined,
      warehouse_code: code,
      due_at: date ? new Date(date).toISOString() : undefined,
      note: note.trim() || undefined,
      lines: filled.map((line, index) => ({
        line_number: index + 1,
        sku_id: line.sku!.id,
        requested_qty: line.qty.trim(),
      })),
    };

    // Prixod endi TSD dagi erkin qabul sessiyasi (2026-11-01) — qo'lda
    // faqat chiqim buyurtmasi kiritiladi.
    const spec = { service: "picking" as const, path: "/api/v1/picking/orders" };

    request(spec.service, spec.path, { method: "POST", body })
      .then(() => {
        setBusy(false);
        void client.invalidateQueries({ queryKey: [spec.service] });
        close();
      })
      .catch((cause: unknown) => {
        setBusy(false);
        setError(cause);
      });
  };

  const setLine = (key: string, patch: Partial<Line>) =>
    setLines((current) => current.map((line) => (line.key === key ? { ...line, ...patch } : line)));

  return (
    <ConfirmDialog
      open={open}
      title="Yangi zakas"
      description="1C dan kelmagan buyurtmani qo'lda kiritish. Terish topshiriqlari «Terishga chiqarish» bosilgandan keyin tug'iladi."
      confirmLabel="Yaratish"
      busy={busy}
      error={error}
      confirmDisabled={!ready}
      wide
      onClose={close}
      onConfirm={submit}
      extra={
        <div style={{ display: "grid", gap: 12 }}>
          <div className="grid-2">
            <label className="field">
              <span className="field-label">Hujjat raqami</span>
              <input
                className="input mono"
                placeholder="РН-000501"
                value={number}
                onChange={(event) => setNumber(event.target.value)}
              />
              <span className="field-hint">Majburiy. Nakladnoy yoki buyurtma raqami.</span>
            </label>

            <label className="field">
              <span className="field-label">Muddat</span>
              <input
                className="input"
                type="date"
                value={date}
                onChange={(event) => setDate(event.target.value)}
              />
            </label>
          </div>

          <div className="field">
            <span className="field-label">Mijoz</span>
            <EntityPicker<Counterparty>
              service="masterData"
              path="/api/v1/counterparties"
              query={{ is_active: true }}
              placeholder="Nomi bo'yicha qidiring…"
              value={party}
              onChange={setParty}
              render={(row) => row.name}
            />
            <span className="field-hint">Majburiy emas, lekin 1C bilan solishtirishni osonlashtiradi.</span>
          </div>

          {codes.length > 1 && (
            <label className="field">
              <span className="field-label">Ombor</span>
              <select className="input" value={code} onChange={(event) => setWarehouse(event.target.value)}>
                {codes.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>
          )}

          <div className="field">
            <span className="field-label">SKU lar</span>
            <div className="line-editor">
              {lines.map((line) => (
                <div className="line-editor-row" key={line.key}>
                  <EntityPicker<Sku>
                    service="masterData"
                    path="/api/v1/skus"
                    query={{ is_active: true }}
                    placeholder="Artikul, nom yoki shtrix-kod…"
                    value={line.sku}
                    onChange={(sku) => setLine(line.key, { sku })}
                    render={(row) => `${row.article} · ${row.name}`}
                  />
                  <input
                    className="input mono"
                    inputMode="decimal"
                    placeholder="Miqdor"
                    value={line.qty}
                    onChange={(event) => setLine(line.key, { qty: event.target.value })}
                  />
                  <button
                    type="button"
                    className="btn btn-outlined btn-sm"
                    aria-label="SKU ni olib tashlash"
                    disabled={lines.length === 1}
                    onClick={() => setLines((current) => current.filter((row) => row.key !== line.key))}
                  >
                    <Icon name="minus" size={14} />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="btn btn-outlined btn-sm"
              style={{ marginTop: 8, justifySelf: "start" }}
              onClick={() => setLines((current) => [...current, BLANK()])}
            >
              <Icon name="plus" size={14} />
              SKU qo'shish
            </button>
            <span className="field-hint">
              Kamida bitta SKU kerak. To'ldirilmagan qatorlar yuborilmaydi.
            </span>
          </div>

          <label className="field">
            <span className="field-label">Izoh</span>
            <input
              className="input"
              placeholder="Majburiy emas"
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </label>

          <div className="field-hint">
            <b>Keyingi qadam:</b>{" "}
            «Terishga chiqarish» ni bosing — teruvchilarga topshiriq tushadi.
          </div>
        </div>
      }
    />
  );
}
